"use client";

import Image from "next/image";
import { File as FileIcon } from "lucide-react";
import clsx from "clsx";
import { useState } from "react";

import type { Asset } from "../types";
import { buildAssetUrl } from "../utils";

type AssetPreviewProps = {
  asset: Asset;
  size?: number;
  detail?: boolean;
  className?: string;
};

export function AssetPreview({
  asset,
  size = 32,
  detail = false,
  className,
}: AssetPreviewProps) {
  const [failedStorageKey, setFailedStorageKey] = useState<string | null>(null);
  const canPreview =
    asset.mediaType === "image" && failedStorageKey !== asset.storageKey;

  if (!canPreview) {
    return (
      <div
        className={clsx(
          "flex shrink-0 items-center justify-center rounded bg-gray-100 text-gray-500",
          detail ? "h-48 w-full" : "",
          className,
        )}
        style={detail ? undefined : { width: size, height: size }}
        aria-label={`${asset.originalFilename} file`}
      >
        <FileIcon size={detail ? 32 : Math.max(14, Math.round(size * 0.45))} />
      </div>
    );
  }

  return (
    <Image
      src={buildAssetUrl(asset)}
      alt={asset.originalFilename || "Selected image preview"}
      width={detail ? 640 : size}
      height={detail ? 360 : size}
      sizes={detail ? "(min-width: 1024px) 20vw, 100vw" : `${size}px`}
      className={clsx(
        "shrink-0 rounded object-cover",
        detail ? "h-48 w-full object-contain" : "",
        className,
      )}
      unoptimized
      onError={() => setFailedStorageKey(asset.storageKey)}
    />
  );
}
